import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, Injector, afterNextRender, booleanAttribute, computed, effect, inject, input, output, signal, untracked, viewChild } from '@angular/core';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { NOTIFIER } from '@shared/notifier/types';
import { SharedModule } from '@shared/shared.module';
import { PlatformCreateFeedbackAttachment } from '@data/_generated-api/platform-api/models/platform-create-feedback-attachment';
import { ViewportService } from '@core/services/viewport.service';
import { SupportAttachmentIntakeService } from './support-attachment-intake.service';
import { SupportAttachmentDraft } from './support-attachment.models';
import { SUPPORT_MAX_ATTACHMENTS } from './support-image.service';
import { SupportRichTextComponent } from './support-rich-text.component';
import { SupportImageEditorComponent } from './support-image-editor.component';
import { CaptureCancelledError, SupportScreenshotService } from './support-screenshot.service';

const SCREENSHOT_FILE_NAME = 'screenshot.jpg';

/**
 * Screenshot-knop in de composer:
 * - `off`: geen knop;
 * - `in-place`: kader kiezen zonder iets dicht te klappen (notities);
 * - `support-panel`: het support-paneel klapt dicht tijdens het kiezen en gaat
 *   daarna terug open op dezelfde melding, zoals bij een nieuw ticket (reacties).
 */
export type SupportComposerScreenshotMode = 'off' | 'in-place' | 'support-panel';

/**
 * Dichtstbijzijnde verticaal scrollbare voorouder (support-paneel, dock-pane,
 * zwevend venster), of undefined als niets ertussen scrolt.
 */
export function findScrollContainer(element: HTMLElement): HTMLElement | undefined {
  let current = element.parentElement;
  while (current && current !== document.body && current !== document.documentElement) {
    const overflowY = getComputedStyle(current).overflowY;
    if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) return current;
    current = current.parentElement;
  }
  return undefined;
}

export interface SupportCommentPayload {
  body: string;
  attachments: PlatformCreateFeedbackAttachment[];
}

/**
 * Reactieveld met sumiere opmaak (WYSIWYG via support-rich-text) en tot drie
 * afbeeldingen via Ctrl+V, de plak-knop, een bestand of (opt-in via
 * `screenshot`) een screenshot met de eigen snip-tool; de beeldeditor
 * (uitsnijden, kaders, pijlen) opent dan in een dialoog. De toestand is
 * vluchtig en leeft hier; in support blijft hij bewaard tijdens een screenshot
 * omdat de paneelinhoud gemount blijft terwijl het paneel dicht is. De eigenaar
 * verstuurt de payload en roept bij succes `clear()` aan.
 */
@Component({
  selector: 'support-comment-composer',
  imports: [SharedModule, TranslocoModule, SupportRichTextComponent, SupportImageEditorComponent],
  templateUrl: './support-comment-composer.component.html',
  styleUrl: './support-comment-composer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SupportCommentComposerComponent {
  /** Bezig-status van de eigenaar (API-call); blokkeert invoer en verzenden. */
  readonly sending = input(false);
  /**
   * Opt-in-overrides voor hergebruik buiten support (notitievelden, spec
   * notable moments): eigen placeholder/knoptekst/icoon, een lege tekst
   * toestaan (notitie optioneel) en een extra verzend-blokkade van de eigenaar
   * (bv. zolang er geen chart gekozen is). Defaults = het support-gedrag.
   */
  readonly placeholder = input<string | null>(null);
  readonly sendLabel = input<string | null>(null);
  readonly sendIcon = input('paper-plane');
  readonly allowEmpty = input(false);
  readonly sendDisabled = input(false);
  /** Ticket-plafond bereikt (12 over melding + reacties): geen nieuwe afbeeldingen, tekst kan nog. */
  readonly attachmentsDisabled = input(false);
  /** Voorinvulling voor bewerk-flows (notitievelden); elke nieuwe waarde vervangt de tekst. */
  readonly initialText = input<string | null>(null);
  /** Eigen verzendknop verbergen; de eigenaar roept dan zelf `submit()` aan (momentdialoog, knop onderaan). */
  readonly hideSend = input(false, { transform: booleanAttribute });
  /**
   * Screenshot-knop naast plakken/bestand, zie {@link SupportComposerScreenshotMode}.
   * Niet op phone: daar bestaat native tab-capture niet en is kaders trekken onwerkbaar.
   */
  readonly screenshot = input<SupportComposerScreenshotMode>('off');
  readonly send = output<SupportCommentPayload>();

  private readonly intake = inject(SupportAttachmentIntakeService);
  /**
   * Screenshot-service en notifier pas bij gebruik ophalen: de meeste composers
   * (support-reacties) hebben ze nooit nodig en hoeven de paneel-afhankelijkheden
   * van de screenshot-service dus ook niet op te bouwen.
   */
  private readonly injector = inject(Injector);
  private readonly transloco = inject(TranslocoService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  /**
   * Scrollpositie van de container rond de composer op het moment van de
   * screenshot-klik. Na de flow (paneel dicht/open, editor-dialoog) komt die
   * terug en schuift de container net genoeg om de nieuwe afbeelding te tonen;
   * zonder dit stond het paneel na het toevoegen hoger dan bij de klik.
   */
  private scrollAnchor: { container: HTMLElement; top: number } | undefined;
  /** Op touch geen Ctrl+V in het plak-label; de knop zelf blijft (Clipboard API). */
  protected readonly viewport = inject(ViewportService);
  private readonly destroyRef = inject(DestroyRef);

  /** Markup-opslagvorm; de WYSIWYG-editor houdt deze via [(value)] in sync. */
  protected readonly text = signal('');
  protected readonly drafts = signal<SupportAttachmentDraft[]>([]);
  /** Guard tegen dubbelklik op de plak-knop zolang het klembord gelezen wordt. */
  protected readonly pasting = signal(false);
  /** Base64-fase tussen de verzendklik en het send-event. */
  protected readonly preparing = signal(false);
  /** Snip-modus loopt (kader kiezen + vastleggen). */
  protected readonly capturing = signal(false);
  /** Vastgelegde screenshot in de beeldeditor-dialoog; undefined = dialoog dicht. */
  protected readonly editorSource = signal<Blob | undefined>(undefined);

  protected readonly maxAttachments = SUPPORT_MAX_ATTACHMENTS;
  protected readonly attachmentsFull = computed(() => this.attachmentsDisabled() || this.drafts().length >= SUPPORT_MAX_ATTACHMENTS);
  protected readonly busy = computed(() => this.sending() || this.preparing());
  protected readonly canSend = computed(() =>
    (this.allowEmpty() || this.text().trim().length > 0) && !this.busy() && !this.sendDisabled());

  private readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('composerFile');
  private readonly richText = viewChild(SupportRichTextComponent);

  private alive = true;

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.alive = false;
      this.revokeAll();
    });

    // Bewerk-flows: elke nieuwe initialText vervangt de tekst (null = niets doen,
    // zodat de gewone verzend-flows onaangeroerd blijven).
    effect(() => {
      const initial = this.initialText();
      if (initial !== null) {
        untracked(() => this.text.set(initial));
      }
    });
  }

  /** Platte tekst op de cursorpositie van de notitie zetten (eigenaar-knoppen zoals "Zet in tekst"). */
  insertText(text: string): void {
    this.richText()?.insertText(text);
  }

  /** Na een geslaagde verzending door de eigenaar: alles leeg voor de volgende reactie. */
  clear(): void {
    this.revokeAll();
    this.text.set('');
    this.drafts.set([]);
  }

  /** Ctrl+V binnen de composer: alleen afbeeldingen onderscheppen, tekst plakt gewoon (in de editor). */
  protected onPaste(event: ClipboardEvent): void {
    if (this.busy()) return;
    const image = this.intake.imageFromPasteEvent(event);
    if (!image) return;
    event.preventDefault();
    void this.addBlob(image.blob, image.fileName);
  }

  protected async pasteFromClipboard(): Promise<void> {
    if (this.attachmentsFull() || this.pasting() || this.busy()) return;
    this.pasting.set(true);
    try {
      const image = await this.intake.readClipboardImage();
      if (image) await this.addBlob(image.blob, image.fileName);
    } finally {
      if (this.alive) this.pasting.set(false);
    }
  }

  protected chooseFile(): void {
    this.fileInput()?.nativeElement.click();
  }

  protected async capture(): Promise<void> {
    if (this.attachmentsFull() || this.capturing() || this.busy()) return;
    this.capturing.set(true);
    const container = findScrollContainer(this.host.nativeElement);
    this.scrollAnchor = container ? { container, top: container.scrollTop } : undefined;
    try {
      const screenshots = this.injector.get(SupportScreenshotService);
      const blob = await (this.screenshot() === 'support-panel'
        ? screenshots.capturePage()
        : screenshots.captureRegion());
      if (!this.alive) return;
      this.editorSource.set(blob);
    } catch (error) {
      this.restoreScroll();
      // Annuleren (Escape) is een keuze van de gebruiker: stil.
      if (error instanceof CaptureCancelledError) return;
      console.error('[composer] screenshot mislukt', error);
      this.injector.get(NOTIFIER).error(this.transloco.translate<string>('support.form.attachments.captureFailed'));
    } finally {
      if (this.alive) this.capturing.set(false);
    }
  }

  /** "Gebruiken" in de beeldeditor: het bewerkte beeld wordt een bijlage. */
  protected onEdited(blob: Blob): void {
    this.editorSource.set(undefined);
    void this.addBlob(blob, SCREENSHOT_FILE_NAME).then(() => this.revealNewAttachment());
  }

  /**
   * Annuleren in de editor of de dialoog sluiten. Na "Gebruiken" is de bron al
   * leeg (onEdited) en meldt de dialoog enkel nog dat hij dicht is: dan niets
   * doen, zodat het scrollanker voor revealNewAttachment blijft staan.
   */
  protected closeEditor(): void {
    if (this.editorSource() === undefined) return;
    this.editorSource.set(undefined);
    this.restoreScroll();
  }

  /** Scrollpositie van vóór de screenshot terugzetten (annuleren of mislukken). */
  private restoreScroll(): void {
    const anchor = this.scrollAnchor;
    this.scrollAnchor = undefined;
    if (anchor) anchor.container.scrollTop = anchor.top;
  }

  /**
   * Na het toevoegen: eerst de positie van vóór de klik terug, dan net genoeg
   * scrollen om de nieuwe afbeelding (onder het tekstveld) te tonen.
   */
  private revealNewAttachment(): void {
    if (!this.alive) return;
    afterNextRender(() => {
      this.restoreScroll();
      const thumbs = this.host.nativeElement.querySelectorAll<HTMLElement>('[data-test-id="support-composer-attachment"]');
      thumbs[thumbs.length - 1]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }, { injector: this.injector });
  }

  protected onFileChosen(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const file = inputElement.files?.[0];
    inputElement.value = '';
    if (!file) return;
    void this.addBlob(file, file.name);
  }

  protected removeAttachment(id: string): void {
    const draft = this.drafts().find(candidate => candidate.id === id);
    if (!draft) return;
    URL.revokeObjectURL(draft.previewUrl);
    this.drafts.update(list => list.filter(candidate => candidate.id !== id));
  }

  /** Public: ook van buitenaf aanroepbaar (header-save in het notitiepaneel). */
  async submit(): Promise<void> {
    if (!this.canSend()) return;
    this.preparing.set(true);
    try {
      const attachments = await this.intake.toPayloads(this.drafts());
      if (!this.alive) return;
      this.send.emit({ body: this.text().trim(), attachments });
    } finally {
      if (this.alive) this.preparing.set(false);
    }
  }

  private async addBlob(blob: Blob, fileName: string): Promise<void> {
    if (this.attachmentsFull()) return;
    const draft = await this.intake.normalizeWithFeedback(blob, fileName);
    if (!draft) return;
    if (!this.alive || this.attachmentsFull()) {
      URL.revokeObjectURL(draft.previewUrl);
      return;
    }
    this.drafts.update(list => [...list, draft]);
  }

  private revokeAll(): void {
    this.drafts().forEach(draft => URL.revokeObjectURL(draft.previewUrl));
  }
}
